import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { Admin } from './pages/Admin';
import { GRC } from './pages/GRC';
import { UnifiedGRCDashboard } from './components/grc/UnifiedGRCDashboard';
import { PublicTrainingPortal } from './pages/PublicTrainingPortal';
import { PublicTrustCenter } from './pages/PublicTrustCenter';
import { authService } from './services/authService';
import { ThemeProvider } from './context/ThemeContext';
import { I18nProvider } from './context/I18nContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { IngestionProgressProvider } from './context/IngestionProgressContext';
import { ToastProvider } from './context/ToastContext';

const ProtectedRoute = ({ children, adminOnly = false }: { children: React.ReactNode, adminOnly?: boolean }) => {
  const { isAuthenticated, isLoading, isOrgAdmin, isGlobalAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isOrgAdmin && !isGlobalAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <IngestionProgressProvider>
            <Router>
            <Routes>
              {/* Public routes - no authentication required */}
              <Route path="/login" element={<Login />} />
              <Route path="/training-portal" element={<PublicTrainingPortal />} />
              <Route path="/trust" element={<PublicTrustCenter />} />
              <Route path="/trust-center" element={<PublicTrustCenter />} />
              
              {/* Protected routes */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute adminOnly>
                    <Admin />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/grc" 
                element={
                  <ProtectedRoute>
                    <GRC />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/unified" 
                element={
                  <ProtectedRoute>
                    <UnifiedGRCDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <Navigate to="/unified" replace />
                  </ProtectedRoute>
                } 
              />
            </Routes>
          </Router>
          </IngestionProgressProvider>
        </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
